import { Injectable } from "@angular/core";
import { US_COUNTRY_ID, UserContext, UserRegion } from "./UserContext";
import { BehaviorSubject, filter, firstValueFrom, take } from "rxjs";
import { Organization } from "../../feature/organizations/models/Organization";
import { CountryService } from "../../shared/reference/country.service";
import clm from "country-locale-map";
import { getLocaleCode } from "../../utilities/CountryUtility";
import { PortalAccount } from "../../feature/account/PortalAccount";
import { AuthService } from "../config/msal-config/auth.service";

@Injectable({ providedIn: 'root' })
export class UserContextLoader {

    private testingEu: boolean = false;

    private _userContext: UserContext = {
        portalAccount: new BehaviorSubject<PortalAccount | undefined>(undefined),
        countryCode: new BehaviorSubject<string | undefined>(undefined),
        countryName: new BehaviorSubject<string | undefined>(undefined),
        region: new BehaviorSubject<UserRegion | undefined>(undefined),
        localeCode: new BehaviorSubject<string>('en-US'),
        currencyCode: new BehaviorSubject<string>('USD'),
        contextLoaded: new BehaviorSubject<boolean>(true)
    };

    constructor(
        private authService: AuthService,
        private countryService: CountryService
    ) {}

    async load() {
        try {
            // console.debug("user-context-loader.load()")
            this.setRegion(UserRegion.NA);

            const account = await firstValueFrom(this.authService.portalAccount.pipe(
                filter(account => account != null && account.token != null),
                take(1)
            ));
            this.handlePortalAccountChange(account);

            const countryCode = await firstValueFrom(this._userContext.countryCode.pipe(
                filter(countryCode => countryCode != null),
                take(1)
            ));
            this.handleCountryCodeChange(countryCode);
        } catch (e: unknown) {
            if(e instanceof Error) {
                throw new Error(`Error while loading user context: ${e.message}. Account details" ${JSON.stringify(this.authService.portalAccount)}`);
            } else {
                throw e;
            }
        }
    }

    private handlePortalAccountChange(account: PortalAccount | undefined) {
        if (!account) {
            throw new Error(`Account is null when loading user context`);
        }

        this._userContext.portalAccount.next(account);
    }

    private handleOrganizationChange(organization: Organization | undefined) {
        if (!organization) {
            throw new Error(`Organization is null when loading user context`);
        }

        const countryId = this.testingEu ? 158 : (organization.address.country_id || US_COUNTRY_ID);
        this.setCountryDetails(countryId);
    }

    private handleCountryCodeChange(countryCode: string | undefined) {
        if (!countryCode) {
            throw new Error(`Country code is null when loading user context`);
        }

        const localeCode = getLocaleCode(countryCode);
        this._userContext.localeCode.next(localeCode);

        const countryInfo = clm.getCountryByAlpha2(countryCode);
        const currencyCode = countryInfo?.currency ?? 'USD';
        this.setCurrencyCode(currencyCode);

        this._userContext.contextLoaded.next(true);
    }

    private setRegion(region: UserRegion) {
        this._userContext.region.next(region);
    }

    private setCurrencyCode(currencyCode: string) {
        this._userContext.currencyCode.next(currencyCode);
    }

    private async setCountryDetails(countryId: number) {
        if (!this._userContext.region.getValue()) {
            throw new Error(`Organization is null when attempting to set country details for countryId when loading user context. CountryId: ${countryId}`);
        }

        const country = await this.countryService.getCountryFromId(countryId);
        if (!country) {
            throw new Error(`Country not found for ID: ${countryId}`);
        }

        this._userContext.countryCode.next(country.code);
        this._userContext.countryName.next(country.display_text);
    }

    public get userContext(): UserContext {
        return this._userContext;
    }
}